// @ts-nocheck
/*
 * Port of views/dashboard/ivr/voice-library/add-voice.js (417 lines). BYTE-IDENTICAL.
 *
 * Upload one prompt with multiple language/file pairs. Filename must match
 * `^[a-zA-Z0-9_-]+$`, at least one language row with a picked file.
 * Voice-type dropdown is filtered through voiceLibraryAcl's
 * `filterVoiceTypesByAcl(moduleOperations, isSuperAdmin)`.
 *
 * V1 QUIRKS preserved verbatim:
 *  - `sendCategory` / `category` state pair exists but is never wired into
 *    the UI (dead state); the useEffect that clears `sendCategory` when it
 *    falls out of `category` never fires. Kept exactly.
 *  - Success modal uses LottieAnimation; v2 policy drops Lottie for a
 *    plain react-bootstrap Modal with just the message.
 *  - The success text for `voice_type === 'incoming'` differs â€” no
 *    "Please wait for Admin Approval" tail.
 *  - `filterVoiceTypesByAcl(moduleOperations, isSuperAdmin)` is called as
 *    the fallback INSIDE render on every re-render when `allowedVoiceTypes`
 *    is empty; matches v1.
 *  - `postApiMediaCall` posts to `API_CREATE_PROMPTS` â€” the same POST that
 *    the standalone voice-library upload uses; hard-coded prompt_category
 *    = "general".
 *  - `getApiCall` for tts-languages is imported from v1's separate
 *    `service/getApiCall` file; v2 exposes the equivalent from httpCall.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card as ShadCard } from '@/components/ui/card';
import { Button as _ShadButton } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const _cx = (...args: any[]) => args.filter(Boolean).join(' ');
const Row: any = ({ className, children, ...props }: any) => (
  <div className={_cx('row', className)} {...props}>
    {children}
  </div>
);
const _colCls = ({ xs, sm, md, lg, xl }: any) => {
  const parts = [];
  if (md) parts.push(`col-md-${md}`);
  if (lg) parts.push(`col-lg-${lg}`);
  if (xl) parts.push(`col-xl-${xl}`);
  if (sm) parts.push(`col-sm-${sm}`);
  if (xs) parts.push(`col-${xs}`);
  if (!parts.length) parts.push('col');
  return parts.join(' ');
};
const Col: any = ({ xs, sm, md, lg, xl, className, children, ...props }: any) => (
  <div className={_cx(_colCls({ xs, sm, md, lg, xl }), className)} {...props}>
    {children}
  </div>
);
const _btnVariantMap: Record<string, any> = {
  primary: 'default',
  secondary: 'outline',
  danger: 'destructive',
  light: 'outline',
  link: 'link',
};
const Button: any = ({ variant, size, className, children, ...props }: any) => {
  let mapped = variant;
  if (typeof variant === 'string') {
    if (variant.startsWith('outline-')) mapped = 'outline';
    else if (_btnVariantMap[variant]) mapped = _btnVariantMap[variant];
    else if (variant.startsWith('btn ')) mapped = 'default';
  }
  return (
    <_ShadButton variant={mapped} size={size} className={className} {...props}>
      {children}
    </_ShadButton>
  );
};
const Card: any = ({ className, children, ...props }: any) => (
  <ShadCard className={className} {...props}>
    {children}
  </ShadCard>
);
Card.Header = ({ className, children, ...props }: any) => (
  <div className={_cx('card-header', className)} {...props}>
    {children}
  </div>
);
Card.Body = ({ className, children, ...props }: any) => (
  <div className={_cx('card-body', className)} {...props}>
    {children}
  </div>
);
Card.Title = ({ as: As = 'h5', className, children, ...props }: any) => (
  <As className={_cx('card-title', className)} {...props}>
    {children}
  </As>
);
const Form: any = ({ children, onSubmit, className, ...props }: any) => (
  <form
    className={className}
    onSubmit={onSubmit || ((e: any) => e.preventDefault())}
    {...props}
  >
    {children}
  </form>
);
Form.Group = ({ className, controlId: _c, children, ...props }: any) => (
  <div className={_cx('mb-3', className)} {...props}>
    {children}
  </div>
);
Form.Label = ({ className, children, htmlFor, ...props }: any) => (
  <label className={_cx('form-label', className)} htmlFor={htmlFor} {...props}>
    {children}
  </label>
);
Form.Control = ({ as, className, children, isInvalid, ...props }: any) => {
  const cls = _cx('form-control', isInvalid && 'is-invalid', className);
  if (as === 'select')
    return (
      <select className={cls} {...props}>
        {children}
      </select>
    );
  if (as === 'textarea') return <textarea className={cls} {...props} />;
  return <input className={cls} {...props} />;
};
Form.Select = ({ className, children, isInvalid, ...props }: any) => (
  <select
    className={_cx('form-control form-select', isInvalid && 'is-invalid', className)}
    {...props}
  >
    {children}
  </select>
);
Form.Check = ({
  type = 'checkbox',
  label,
  id,
  className,
  inline,
  children,
  ...props
}: any) => (
  <div className={_cx('form-check', inline && 'form-check-inline', className)}>
    <input type={type} id={id} className="form-check-input" {...props} />
    {label !== undefined && (
      <label htmlFor={id} className="form-check-label">
        {label}
      </label>
    )}
    {children}
  </div>
);
const Modal: any = ({ show, onHide, size, centered: _c, children, className, ...props }: any) => {
  const sizeCls =
    size === 'sm'
      ? 'sm:max-w-md'
      : size === 'lg'
      ? 'sm:max-w-2xl'
      : size === 'xl'
      ? 'sm:max-w-6xl'
      : '';
  return (
    <Dialog open={!!show} onOpenChange={(o: boolean) => !o && onHide?.()}>
      <DialogContent
        className={`${sizeCls} max-h-[90vh] overflow-y-auto ${className || ''}`.trim()}
        {...props}
      >
        {children}
      </DialogContent>
    </Dialog>
  );
};
Modal.Header = ({ children, closeButton: _cb, className, ...props }: any) => (
  <DialogHeader className={className} {...props}>
    {children}
  </DialogHeader>
);
Modal.Title = ({ children, className, ...props }: any) => (
  <DialogTitle className={className} {...props}>
    {children}
  </DialogTitle>
);
Modal.Body = ({ children, className, ...props }: any) => (
  <div className={`overflow-x-auto ${className || ''}`.trim()} {...props}>
    {children}
  </div>
);
Modal.Footer = ({ children, className, ...props }: any) => (
  <DialogFooter className={className} {...props}>
    {children}
  </DialogFooter>
);
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import {
  Play,
  Square,
  Upload as UploadIcon,
  Trash2,
  Plus,
  FileAudio,
  ArrowLeft,
  Mic,
  Loader2,
  CheckCircle2,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { getApiCall, postApiMediaCall } from '@/service/httpCall';
import { fetchAndSetFlashMessage } from '@/service/httpCall';
import {
  API_CREATE_PROMPTS,
  API_EDIT_PROMPT_LANGUAGE_FILE,
} from '@/service/url';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  ROUTES,
  USER_ROLES,
  FLASH_MESSAGE_ACTION_OBJECT,
  MODULE_ACL,
} from '@/utilities/constant';
import { getUser } from '@/utilities/setting';
import { parseModuleOperations } from '@/utilities/voicePlanAcl';
import { filterLanguagesByAcl, filterVoiceTypesByAcl } from '@/utilities/voiceLibraryAcl';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import '@/assets/custom/css/ivr-editor.css';

import { canUploadPromptsOrMoh } from '@/utilities/promptUploadAccess';
import { UploadNotPermitted } from '@/components/feedback/UploadNotPermitted';

const AddVoice = () => {
  const [category, setCategory] = useState<any[]>([]);
  const [fileName, setFileName] = useState('');
  const [fileNameError, setFileNameError] = useState('');
  const [showToastModal, setShowToastModal] = useState(false);
  const [sendCategory, setSendcategory] = useState<any>(null);
  const [toastMessage, setToastMessage] = useState('');
  const [allLanguages, setAllLanguages] = useState<any[]>([]);
  const navigate = useNavigate();
  const { id: promptId } = useParams();
  const location = useLocation();
  const isEditMode = Boolean(promptId);
  const [loadingPrompt, setLoadingPrompt] = useState(isEditMode);
  const [isUploading, setIsUploading] = useState(false);
  const [activeModule, setActiveModules] = useState<any[]>([]);
  const [moduleOperations, setModuleOperations] = useState<any>({});
  const [userRole, setUserRole] = useState<any>(undefined);
  const [alertMessage, setAlertMessage] = useState<any>(null);
  const [voiceType, setVoiceType] = useState('');
  const [allowedVoiceTypes, setAllowedVoiceTypes] = useState<any[]>([]);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const languages = useMemo(
    () => filterLanguagesByAcl(moduleOperations, isSuperAdmin, allLanguages),
    [moduleOperations, isSuperAdmin, allLanguages],
  );

  // Each pair: { id, language, file }
  const [langFilePairs, setLangFilePairs] = useState<any[]>([
    { id: 1, language: '', file: null },
  ]);
  const nextId = useRef(2);

  const [playingId, setPlayingId] = useState<any>(null);
  const audioRef = useRef<any>(null);
  const audioUrlRef = useRef<any>(null);

  const stopPreview = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
      audioUrlRef.current = null;
    }
    setPlayingId(null);
  };

  const togglePreview = (pair: any) => {
    if (!pair.file) return;
    if (playingId === pair.id) {
      stopPreview();
      return;
    }
    stopPreview();
    const url = URL.createObjectURL(pair.file);
    const audio = new Audio(url);
    audio.onended = stopPreview;
    audio.onerror = () => {
      toast.error('Unable to play selected audio.');
      stopPreview();
    };
    audioRef.current = audio;
    audioUrlRef.current = url;
    audio
      .play()
      .then(() => setPlayingId(pair.id))
      .catch(() => stopPreview());
  };

  useEffect(() => () => stopPreview(), []);

  useEffect(() => {
    const resp = getUser();
    if (resp) {
      setUserRole(resp.role);
      const superAdmin = resp.role === USER_ROLES.SUPERADMIN;
      setIsSuperAdmin(superAdmin);
      if (resp?.acl_settings) {
        const ops = parseModuleOperations(resp.acl_settings.module_operations);
        setModuleOperations(ops);
        const modules = getKeysWithTrueValues(ops);
        setActiveModules(modules);
        setAllowedVoiceTypes(filterVoiceTypesByAcl(ops, superAdmin));
      } else if (superAdmin) {
        setAllowedVoiceTypes(filterVoiceTypesByAcl(null, true));
      }
    }
    fetchAndSetFlashMessage(
      (msg: any) =>
        msg.module_name === MODULE_ACL.VOICE_LIBRARY &&
        msg.action === FLASH_MESSAGE_ACTION_OBJECT.ADD &&
        msg.type === 'Flash Message',
      setAlertMessage,
    );
  }, []);

  function getKeysWithTrueValues(moduleOperations: any) {
    return Object.keys(moduleOperations).filter((key) => {
      const permissions = moduleOperations[key];
      return Object.values(permissions).some((value) => value === true);
    });
  }

  const showToasts = (message: string) => {
    setToastMessage(message);
    setShowToastModal(true);
    setTimeout(() => setShowToastModal(false), 3000);
  };

  const addPair = () => {
    setLangFilePairs((prev) => [
      ...prev,
      { id: nextId.current++, language: '', file: null },
    ]);
  };

  const removePair = (id: any) => {
    if (playingId === id) stopPreview();
    setLangFilePairs((prev) => prev.filter((p: any) => p.id !== id));
  };

  const updatePairLang = (id: any, lang: string) => {
    setLangFilePairs((prev) =>
      prev.map((p: any) =>
        p.id === id ? { ...p, language: lang, file: null } : p,
      ),
    );
  };

  const updatePairFile = (id: any, file: any) => {
    if (playingId === id) stopPreview();
    setLangFilePairs((prev) =>
      prev.map((p: any) => (p.id === id ? { ...p, file } : p)),
    );
  };

  const usedLanguages = langFilePairs.map((p: any) => p.language).filter(Boolean);

  const validateFileName = (name: string) => {
    const validPattern = /^[a-zA-Z0-9_-]+$/;
    if (name.trim() === '') {
      setFileNameError('File name is required');
      return false;
    }
    if (name.includes(' ')) {
      setFileNameError('File name cannot contain spaces');
      return false;
    }
    if (!validPattern.test(name)) {
      setFileNameError(
        'File name can only contain letters, numbers, underscore (_), and hyphen (-)',
      );
      return false;
    }
    setFileNameError('');
    return true;
  };

  const handleFileNameChange = (e: any) => {
    const value = e.target.value;
    setFileName(value);
    if (value.trim() !== '') {
      validateFileName(value);
    } else {
      setFileNameError('');
    }
  };

  const handleUpload = async () => {
    if (!fileName.trim() || !voiceType) {
      toast.error('Please fill all required fields!');
      return;
    }
    if (!validateFileName(fileName)) {
      toast.error('Please fix the file name errors before uploading');
      return;
    }
    for (const pair of langFilePairs) {
      if (!pair.language) {
        toast.error('Please select a language for each row.');
        return;
      }
      if (!pair.file) {
        const langLabel =
          languages.find((l: any) => l.value === pair.language)?.label ||
          pair.language;
        toast.error(`Please select a file for language: ${langLabel}`);
        return;
      }
    }

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('prompt_name', fileName);
      formData.append('prompt_category', 'general');
      formData.append('voice_type', voiceType);
      for (const pair of langFilePairs) {
        formData.append('files', pair.file);
        formData.append('languages', pair.language);
      }
      await postApiMediaCall(API_CREATE_PROMPTS, formData);
      const msg =
        voiceType === 'incoming'
          ? 'Voice file(s) uploaded successfully'
          : 'Voice file(s) uploaded successfully, Please wait for Admin Approval';
      showToasts(msg);
      setTimeout(
        () => navigate(`/${ROUTES.VOICE_LIBRARY.VIEW_VOICE_FILES}`),
        1500,
      );
    } catch (err: any) {
      toast.error(friendlyApiError(err) || 'Upload failed');
    } finally {
      setIsUploading(false);
    }
  };

  const fetchLanguages = () => {
    getApiCall('/v2/tts-languages').then((response: any) => {
      if (Array.isArray(response)) {
        const transformed = response.map((lang: any) => ({
          label: lang.language,
          value: lang.code,
        }));
        setAllLanguages(transformed);
      }
    });
  };

  useEffect(() => {
    fetchLanguages();
  }, []);

  useEffect(() => {
    if (voiceType && !allowedVoiceTypes.some((o: any) => o.value === voiceType)) {
      setVoiceType('');
    }
  }, [allowedVoiceTypes, voiceType]);

  useEffect(() => {
    if (sendCategory && !category.includes(sendCategory)) {
      setSendcategory(null);
    }
  }, [category, sendCategory]);

  const voiceTypeOptions =
    allowedVoiceTypes.length > 0
      ? allowedVoiceTypes
      : filterVoiceTypesByAcl(moduleOperations, isSuperAdmin);

  return (
    <FillPage>
      {alertMessage && (
        <div
          className="mb-2 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-4 py-2 text-sm text-emerald-700 dark:text-emerald-400"
          role="status"
        >
          <span className="ivr-alert-marquee">
            <span
              className="ivr-alert-marquee-text"
              style={{ '--scroll-dur': `${Math.max(18, alertMessage.length * 0.16)}s` } as any}
            >
              {alertMessage}
            </span>
          </span>
        </div>
      )}
      <PageHeader
        title="Upload Voice File"
        description="Bring audio into the IVR library — pick a WAV/MP3 file per language."
        actions={
          <>
            <Button asChild variant="outline">
              <Link to={`/${ROUTES.VOICE_LIBRARY.VIEW_VOICE_FILES}`}>
                <ArrowLeft className="h-4 w-4" />
                Back
              </Link>
            </Button>
            {userRole === USER_ROLES.CALL_CENTRE_ADMIN &&
              activeModule?.includes('TTS') && (
                <Button onClick={() => navigate(`/${ROUTES.TTS.CREATE}`)}>
                  <Mic className="h-4 w-4" />
                  Add TTS
                </Button>
              )}
          </>
        }
      />

      <div
        className="rounded-2xl border border-border/60 bg-card p-5"
        style={{
          boxShadow:
            '6px 6px 14px rgba(163, 177, 198, 0.35), -6px -6px 14px rgba(255, 255, 255, 0.9)',
        }}
      >
        <div className="mb-4 flex items-center gap-3 border-b border-border/50 pb-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary ring-1 ring-inset ring-primary/25">
            <UploadIcon className="h-5 w-5" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-foreground">
              New voice prompt
            </h3>
            <p className="text-[11px] text-muted-foreground">
              Upload approved WAV / MP3 clips per language for this prompt.
            </p>
          </div>
        </div>

        <form onSubmit={(e) => e.preventDefault()}>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-1.5">
              <label htmlFor="fileName" className="text-xs font-semibold text-foreground">
                File name <span className="text-destructive">*</span>
              </label>
              <input
                type="text"
                id="fileName"
                value={fileName}
                onChange={handleFileNameChange}
                onKeyDown={(e) => {
                  if (e.key === ' ') e.preventDefault();
                }}
                onBlur={() => validateFileName(fileName)}
                placeholder="e.g. welcome-en-male"
                className={`input-embossed h-10 w-full rounded-md px-3 text-sm ${
                  fileNameError ? 'ring-2 ring-destructive/40' : ''
                }`}
              />
              {fileNameError ? (
                <p className="text-[11px] text-destructive">{fileNameError}</p>
              ) : (
                <p className="text-[11px] text-muted-foreground">
                  Letters, numbers, underscore (_), and hyphen (-) only.
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <label htmlFor="voiceType" className="text-xs font-semibold text-foreground">
                Voice type <span className="text-destructive">*</span>
              </label>
              <select
                id="voiceType"
                value={voiceType}
                onChange={(e) => setVoiceType(e.target.value)}
                className="input-embossed h-10 w-full rounded-md px-3 text-sm"
              >
                <option value="">Select voice type…</option>
                {voiceTypeOptions.map((opt: any) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-5 flex items-center justify-between">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Language &amp; file <span className="text-destructive">*</span>
            </h4>
            {langFilePairs.length < languages.length && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addPair}
                className="gap-1.5"
              >
                <Plus className="h-3.5 w-3.5" />
                Add language
              </Button>
            )}
          </div>

          <div className="mt-2 space-y-3">
            {langFilePairs.map((pair: any) => {
              const availableLangs = languages.filter(
                (l: any) =>
                  l.value === pair.language ||
                  !usedLanguages.includes(l.value),
              );
              const isPlaying = playingId === pair.id;
              return (
                <div
                  key={pair.id}
                  className="rounded-xl border border-border/60 bg-muted/20 p-3"
                >
                  <div className="flex flex-wrap items-start gap-3">
                    <div className="w-full space-y-1.5 sm:w-[220px]">
                      <label className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                        Language
                      </label>
                      <select
                        value={pair.language}
                        onChange={(e) => updatePairLang(pair.id, e.target.value)}
                        className="input-embossed h-9 w-full rounded-md px-2.5 text-xs"
                      >
                        <option value="">Select language…</option>
                        {availableLangs.map((l: any) => (
                          <option key={l.value} value={l.value}>
                            {l.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {pair.language && (
                      <div className="min-w-0 flex-1 space-y-1.5">
                        <label className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                          Audio file
                        </label>
                        <input
                          type="file"
                          accept=".wav,.mp3"
                          onChange={(e: any) =>
                            updatePairFile(pair.id, e.target.files[0] || null)
                          }
                          className="input-embossed h-9 w-full rounded-md px-2 py-1 text-xs file:mr-3 file:rounded-md file:border-0 file:bg-primary/10 file:px-3 file:py-1 file:text-xs file:font-semibold file:text-primary hover:file:bg-primary/15"
                        />
                        {pair.file && (
                          <div className="mt-1 flex items-center gap-2 rounded-md border border-border/60 bg-card px-2 py-1.5">
                            <button
                              type="button"
                              onClick={() => togglePreview(pair)}
                              title={isPlaying ? 'Stop' : 'Listen'}
                              className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary ring-1 ring-inset ring-primary/20 transition hover:bg-primary/15"
                            >
                              {isPlaying ? (
                                <Square className="h-3 w-3" />
                              ) : (
                                <Play className="h-3 w-3" />
                              )}
                            </button>
                            <FileAudio className="h-3.5 w-3.5 text-muted-foreground" />
                            <span className="truncate text-[11px] font-medium text-foreground">
                              {pair.file.name}
                            </span>
                            <span className="ml-auto text-[10px] text-muted-foreground">
                              {(pair.file.size / 1024).toFixed(0)} KB
                            </span>
                          </div>
                        )}
                      </div>
                    )}

                    {langFilePairs.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removePair(pair.id)}
                        title="Remove language"
                        className="mt-6 inline-flex h-8 w-8 items-center justify-center rounded-md bg-rose-100 text-rose-700 ring-1 ring-inset ring-rose-200 transition hover:bg-rose-200 dark:bg-rose-950/40 dark:text-rose-300"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-5 flex items-center justify-end gap-2 border-t border-border/50 pt-4">
            <Button asChild variant="outline">
              <Link to={`/${ROUTES.VOICE_LIBRARY.VIEW_VOICE_FILES}`}>Cancel</Link>
            </Button>
            <Button
              type="button"
              onClick={handleUpload}
              disabled={isUploading}
              className="gap-1.5"
            >
              {isUploading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <UploadIcon className="h-4 w-4" />
              )}
              {isUploading ? 'Uploading…' : 'Upload prompt'}
            </Button>
          </div>
        </form>
      </div>

      <Modal
        show={showToastModal}
        centered
        onHide={() => setShowToastModal(false)}
        size="sm"
      >
        <Modal.Body className="flex flex-col items-center gap-3 px-2 py-6 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-600 ring-1 ring-inset ring-emerald-500/30 dark:text-emerald-400">
            <CheckCircle2 className="h-8 w-8" />
          </div>
          <h5 className="text-base font-semibold text-foreground">Upload successful</h5>
          <p className="text-sm text-muted-foreground">{toastMessage}</p>
        </Modal.Body>
      </Modal>
    </FillPage>
  );
};

const AddVoiceGuarded = () => {
  if (!canUploadPromptsOrMoh()) {
    return <UploadNotPermitted title="Add Voice File" what="voice files" />;
  }

  return <AddVoice />;
};

export default AddVoiceGuarded;
